// @ts-nocheck

/**
 * Deal of the Day - fetch products via Storefront API GraphQL, build slides, init Swiper.
 *
 * Each card renders exactly one CTA:
 *   sold out          -> disabled "Sold Out"
 *   needs quick buy   -> "Add to Bag" that opens the quick-add modal/drawer
 *                       (multi-variant, linked_products, or related_superbundle)
 *   fly bundle parent -> direct bundle add (or "View Bundle" link when metafields are missing)
 *   single variant    -> "Add to Bag" that adds straight to cart
 */
(function (global) {
  "use strict";

  // A refresh or theme reload can inject this file again. Keep one copy of the
  // helpers, but always try to start any section that has not been set up yet.
  if (global.__dealOfTheDayStorefront) {
    if (typeof global.__dealOfTheDayBoot === "function") global.__dealOfTheDayBoot();
    return;
  }
  global.__dealOfTheDayStorefront = true;

  var API_VERSION = "2025-01";
  var CACHE_KEY_PREFIX = "dealOfTheDay_v4_";
  var CACHE_TTL_MS = 10 * 60 * 1000;
  /** Start fetching while the section is still one tenth of a viewport away. */
  var PREFETCH_ROOT_MARGIN = "10% 0px";
  var IMAGE_WIDTHS = [220, 360, 420, 600];
  var IMAGE_SIZES = "(max-width: 600px) 55vw, (min-width: 601px) 33vw, 360px";
  var IMAGE_RENDER_WIDTH = 600;

  var DEAL_DAY_QUERY =
    "query getDealOfTheDayProducts($handle: String!, $first: Int!) {" +
    "  collection(handle: $handle) {" +
    "    products(first: $first) {" +
    "      edges {" +
    "        node {" +
    "          id" +
    "          title" +
    "          handle" +
    "          availableForSale" +
    "          tags" +
    "          featuredImage {" +
    "            url(transform: { maxWidth: 600 })" +
    "            altText" +
    "            width" +
    "            height" +
    "          }" +
    "          priceRange { minVariantPrice { amount currencyCode } }" +
    "          compareAtPriceRange { minVariantPrice { amount currencyCode } }" +
    '          kidYearTag: metafield(namespace: "custom", key: "kid_s_year_tag") { value }' +
    '          flyChildDigitalMap: metafield(namespace: "custom", key: "fly_child_digital_map") { value }' +
    // value only — reference unions need fragments and can 400 the whole query
    '          linkedProducts: metafield(namespace: "custom", key: "linked_products") { value }' +
    '          relatedSuperbundle: metafield(namespace: "custom", key: "related_superbundle") { value }' +
    "          variants(first: 20) {" +
    "            edges {" +
    "              node {" +
    "                id" +
    "                availableForSale" +
    "                quantityAvailable" +
    "                requiresShipping" +
    "                price { amount currencyCode }" +
    "                compareAtPrice { amount currencyCode }" +
    "                image {" +
    "                  url(transform: { maxWidth: 600 })" +
    "                  altText" +
    "                  width" +
    "                  height" +
    "                }" +
    '                includesDigital: metafield(namespace: "custom", key: "includes_digital") { value }' +
    '                flyVariantIds: metafield(namespace: "custom", key: "fly_config_data_product_variant_ids") { value }' +
    '                flyOfferId: metafield(namespace: "custom", key: "fly_gfb_offer_id") { value }' +
    "              }" +
    "            }" +
    "          }" +
    "        }" +
    "      }" +
    "    }" +
    "  }" +
    "}";

  function decodeId(gid) {
    if (!gid) return "";
    if (gid.indexOf("gid://") !== -1) return gid.split("/").pop();
    try {
      var decoded = atob(gid);
      return decoded.indexOf("gid://") !== -1 ? decoded.split("/").pop() : decoded;
    } catch (e) {
      return gid;
    }
  }

  function escapeHtml(value) {
    if (value == null) return "";
    return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  /** Round to whole rupees with thousands separators, matching the Liquid money output. */
  function formatPrice(amount) {
    var num = typeof amount === "string" ? parseFloat(amount) : Number(amount);
    if (!Number.isFinite(num)) return "";
    return Math.round(num)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function currencySymbol(code) {
    return code === "INR" ? "₹" : code || "";
  }

  function imageUrlWithWidth(url, width) {
    if (!url) return "";
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, "$1width=" + width);
    return url + (url.indexOf("?") !== -1 ? "&" : "?") + "width=" + width;
  }

  function buildSrcSet(url) {
    if (!url) return "";
    return IMAGE_WIDTHS.map(function (width) {
      return imageUrlWithWidth(url, width) + " " + width + "w";
    }).join(", ");
  }

  function metafieldValue(node, key) {
    return node && node[key] && node[key].value ? node[key].value : "";
  }

  function plainAgeTag(value) {
    let text = String(value || "").trim();
    if (text.charAt(0) !== "[") return text;
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.length) text = String(parsed[0] || "").trim();
    } catch (e) {}
    return text;
  }

  function readAgeTags(section) {
    const map = {};
    const node = section.querySelector("[data-deal-day-age-tags]");
    if (!node) return map;
    try {
      const list = JSON.parse(node.textContent || "[]");
      if (!list || !list.length) return map;
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        const tag = item && item.tag != null ? plainAgeTag(item.tag) : "";
        if (!tag || item.id == null) continue;
        map[String(item.id)] = tag;
      }
    } catch (e) {}
    return map;
  }

  /** True when a Storefront metafield has a real reference / list payload (not empty). */
  function hasMetafieldPayload(node, key) {
    var value = metafieldValue(node, key);
    if (!value) return false;
    var trimmed = String(value).trim();
    if (!trimmed || trimmed === "[]" || trimmed === "null" || trimmed === "{}") return false;
    return true;
  }

  /**
   * Flatten a GraphQL product node into everything the card markup needs.
   * @param {Object} node - GraphQL product node
   * @param {Object} settings - { sectionId, quickAddColorScheme, addToCartAnimation }
   */
  function normalizeProduct(node, settings) {
    var variantEdges = (node.variants && node.variants.edges) || [];
    var variants = variantEdges.map(function (edge) {
      return edge.node;
    });
    var variant =
      variants.filter(function (v) {
        return v.availableForSale;
      })[0] || variants[0];

    var priceAmount = variant && variant.price ? variant.price.amount : node.priceRange.minVariantPrice.amount;
    var currency = (variant && variant.price ? variant.price.currencyCode : null) || node.priceRange.minVariantPrice.currencyCode;
    var compareAmount =
      variant && variant.compareAtPrice ? variant.compareAtPrice.amount : node.compareAtPriceRange.minVariantPrice.amount;

    var price = parseFloat(priceAmount) || 0;
    var compareAt = parseFloat(compareAmount) || 0;
    var isOnSale = compareAt > price;
    var discountPercent = isOnSale ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

    var tags = node.tags || [];
    var tagsLower = tags.map(function (tag) {
      return String(tag || "").toLowerCase();
    });
    var hasFlyBundleTag = tagsLower.indexOf("fly bundles parent product") !== -1;
    var hasOutOfStockTag = tagsLower.indexOf("out of stock") !== -1;

    var isSoldOut = !variant || !variant.availableForSale || !node.availableForSale;
    if (hasFlyBundleTag && hasOutOfStockTag) isSoldOut = true;

    var image = (variant && variant.image) || node.featuredImage;
    var isDigitalBook = variant
      ? variant.requiresShipping === false || metafieldValue(variant, "includesDigital") === "true"
      : false;

    var flyVariantIds = isSoldOut ? "" : metafieldValue(variant, "flyVariantIds");
    var flyOfferId = isSoldOut ? "" : metafieldValue(variant, "flyOfferId");

    var hasMultipleVariants = variants.length > 1;
    var hasLinkedProducts = hasMetafieldPayload(node, "linkedProducts");
    var hasRelatedSuperbundle = hasMetafieldPayload(node, "relatedSuperbundle");
    // Match quick-add.liquid: open Quick Buy when the shopper must choose an option
    // (Shopify variants, linked products, or Regular/Bundle superbundle).
    var needsQuickBuy = hasMultipleVariants || hasLinkedProducts || hasRelatedSuperbundle;

    return {
      sectionId: settings.sectionId,
      quickAddColorScheme: settings.quickAddColorScheme,
      addToCartAnimation: settings.addToCartAnimation,
      productId: decodeId(node.id),
      variantId: variant ? decodeId(variant.id) : "",
      handle: node.handle,
      url: "/products/" + node.handle,
      title: node.title,
      imageUrl: image ? image.url : "",
      imageAlt: image && image.altText ? image.altText : node.title,
      imageRatio: image && image.width && image.height ? image.height / image.width : 1,
      price: price,
      compareAt: compareAt,
      currencySymbol: currencySymbol(currency),
      isOnSale: isOnSale,
      discountPercent: discountPercent,
      variantsCount: variants.length,
      hasMultipleVariants: hasMultipleVariants,
      needsQuickBuy: needsQuickBuy,
      inventoryQuantity: variant && variant.quantityAvailable != null ? variant.quantityAvailable : 0,
      isAvailable: !isSoldOut,
      isSoldOut: isSoldOut,
      isDigitalBook: isDigitalBook,
      isPreOrder: tagsLower.indexOf("pre_order") !== -1,
      tagsJoined: tags.join(","),
      hasFlyBundleTag: hasFlyBundleTag,
      flyVariantIds: flyVariantIds,
      flyOfferId: flyOfferId,
      flyChildDigitalMap: metafieldValue(node, "flyChildDigitalMap"),
      ageTag:
        plainAgeTag(metafieldValue(node, "kidYearTag")) ||
        (settings.ageTags && settings.ageTags[decodeId(node.id)]) ||
        "",
    };
  }


  /** GTM payload, mirroring the `product-gtm-data` snippet. */
  function buildGtmJson(data) {
    var listPrice = data.isOnSale ? data.compareAt : data.price;
    var discount = listPrice > data.price ? listPrice - data.price : 0;
    return JSON.stringify({
      product_id: data.productId,
      product_title: data.title,
      product_img_url: data.imageUrl ? imageUrlWithWidth(data.imageUrl, 500) : null,
      product_slug: data.handle,
      product_moq: 50,
      product_discount_percent: data.discountPercent || null,
      is_in_stock: data.isAvailable,
      product_price: listPrice,
      product_discount_price: discount > 0 ? discount : null,
      product_discounted_price: data.price,
      product_tag: data.tagsJoined || null,
      preorder: data.isPreOrder ? "yes" : "no",
      product_available_quantity: data.inventoryQuantity,
      product_category_id: null,
      product_sub_category_id: null,
      product_category_slug: null,
      l1_category_name: null,
      l2_category_name: null,
      l3_category_name: null,
      product_variant_id: data.variantId,
    }).replace(/'/g, "\\u0027");
  }

  var CHECK_ICON =
    '<span aria-hidden="true" class="add-to-cart-text--added"><span><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5L7 12.5L13 3.5" stroke="#F1EFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></span>';

  var SPINNER_ICON =
    '<span class="quick-add__button-loader"><svg class="quick-add__button-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="43.98" stroke-dashoffset="10.99"/></svg></span>';

  /** Hidden inputs shared by both quick-add forms. */
  function buildFormFields(data) {
    return (
      '<input type="hidden" name="form_type" value="product">' +
      '<input type="hidden" name="utf8" value="✓">' +
      '<input type="hidden" name="id" ref="variantId" value="' +
      escapeHtml(data.variantId) +
      '">' +
      '<input type="hidden" name="quantity" value="1">' +
      '<input type="hidden" name="properties[_digital_book]" value="' +
      (data.isDigitalBook ? "true" : "false") +
      '">' +
      '<input type="hidden" name="properties[_purchase_type]" value="null">'
    );
  }

  function wrapQuickAddForm(data, variantClass, buttonHtml) {
    var formId = "QuickAdd-ProductForm-" + data.productId + "-" + data.sectionId + "-" + data.slideKey;
    return (
      '<quick-add-component class="quick-add color-' +
      escapeHtml(data.quickAddColorScheme) +
      ' deal-day-quick-add" ref="quickAdd" data-product-title="' +
      escapeHtml(data.title) +
      '">' +
      '<product-form-component data-section-id="' +
      escapeHtml(data.sectionId) +
      '" data-product-id="' +
      escapeHtml(data.productId) +
      "\" data-product-gtm='" +
      buildGtmJson(data) +
      '\' on:submit="/handleSubmit" class="quick-add__product-form-component quick-add__product-form-component--' +
      variantClass +
      '">' +
      '<div class="visually-hidden" aria-live="assertive" role="status" aria-atomic="true" ref="liveRegion"></div>' +
      '<form method="post" action="/cart/add" id="' +
      escapeHtml(formId) +
      '" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">' +
      buildFormFields(data) +
      buttonHtml +
      '<input type="hidden" name="product-id" value="' +
      escapeHtml(data.productId) +
      '"><input type="hidden" name="section-id" value="' +
      escapeHtml(data.sectionId) +
      '">' +
      "</form>" +
      "</product-form-component>" +
      "</quick-add-component>"
    );
  }

  /**
   * Exactly one CTA per card. The branches are mutually exclusive on purpose so a product with a
   * single option but several variants can never render both an "Add" and a "Choose" button.
   * Quick Buy wins over fly-bundle direct ATC so Superbundle / linked options can be chosen.
   */
  function buildCtaHtml(data) {
    if (data.isSoldOut) {
      return (
        '<button class="button quick-add__button quick-add__button--sold-out" type="button" disabled>' +
        '<span class="add-to-cart-text"><span class="add-to-cart-text__content">Sold Out</span></span>' +
        "</button>"
      );
    }

    if (data.needsQuickBuy) {
      var chooseButton =
        '<button class="button quick-add__button quick-add__button--choose quick-add__button--deal-day" type="button" name="add" on:click="quick-add-component/handleClick">' +
        SPINNER_ICON +
        '<span class="add-to-cart-text"><span class="add-to-cart-text__content">Add to Bag</span></span>' +
        "</button>";
      return wrapQuickAddForm(data, "multi-variant", chooseButton);
    }

    if (data.hasFlyBundleTag) {
      if (!data.flyVariantIds || !data.flyOfferId) {
        return (
          '<div class="quick-add quick-add--view-bundle color-' +
          escapeHtml(data.quickAddColorScheme) +
          ' deal-day-quick-add"><a href="' +
          escapeHtml(data.url) +
          '" class="button quick-add__button quick-add__button--view-bundle">' +
          '<span class="add-to-cart-text"><span class="add-to-cart-text__content">View Bundle</span></span></a></div>'
        );
      }
      return (
        '<div class="quick-add color-' +
        escapeHtml(data.quickAddColorScheme) +
        ' deal-day-quick-add">' +
        '<button type="button" class="button quick-add__button quick-add__button--add fly-bundle-add-btn" data-fly-variant-ids="' +
        escapeHtml(data.flyVariantIds) +
        '" data-fly-offer-id="' +
        escapeHtml(data.flyOfferId) +
        '" data-fly-bundle-name="' +
        escapeHtml(data.title) +
        '"' +
        (data.flyChildDigitalMap ? ' data-fly-digital-map="' + escapeHtml(data.flyChildDigitalMap) + '"' : "") +
        ">" +
        '<span class="add-to-cart-text"><span class="add-to-cart-text__content">Add to Bag</span></span>' +
        CHECK_ICON +
        "</button></div>"
      );
    }

    var variantMedia = data.imageUrl ? imageUrlWithWidth(data.imageUrl, 100) : "";
    var addButton =
      '<add-to-cart-component ref="addToCartButtonContainer" data-product-variant-media="' +
      escapeHtml(variantMedia) +
      '" data-add-to-cart-animation="' +
      escapeHtml(data.addToCartAnimation) +
      '" data-is-deal-day="true" data-is-digital-product="' +
      (data.isDigitalBook ? "true" : "false") +
      '">' +
      '<span class="add-to-cart-loader hidden" ref="addToCartLoader" aria-hidden="true"><span class="loader-spinner"></span></span>' +
      '<button type="button" name="add" ref="addToCartButton" on:click="/handleClick" class="button quick-add__button quick-add__button--add quick-add__button--deal-day">' +
      '<span class="add-to-cart-text"><span class="add-to-cart-text__content">Add to Bag</span></span>' +
      CHECK_ICON +
      "</button></add-to-cart-component>";
    return wrapQuickAddForm(data, "single-variant", addButton);
  }

  /**
   * One swiper slide. Structure mirrors the previous Liquid markup so deal-day.css keeps working:
   * the `<a ref="productCardLink">` in particular is what quick-add.js reads to open the modal.
   */
  function buildSlideHtml(data, position, isPriority) {
    var srcUrl = imageUrlWithWidth(data.imageUrl, IMAGE_RENDER_WIDTH);
    var height = Math.round(IMAGE_RENDER_WIDTH * data.imageRatio);
    const ageTagHtml = data.ageTag
      ? '<div class="pw-product-age-tag pw-product-age-tag--deal"><span class="pw-product-age-tag__text">' +
        escapeHtml(data.ageTag) +
        "</span></div>"
      : "";
    var imageHtml = data.imageUrl
      ? '<img class="homedeal-desktop-img" src="' +
        escapeHtml(srcUrl) +
        '" srcset="' +
        escapeHtml(buildSrcSet(data.imageUrl)) +
        '" sizes="' +
        IMAGE_SIZES +
        '" width="' +
        IMAGE_RENDER_WIDTH +
        '" height="' +
        height +
        '" alt="' +
        escapeHtml(data.imageAlt) +
        '" loading="lazy" fetchpriority="low" decoding="async">'
      : "";

    var comparePrice =
      data.isOnSale && data.compareAt > 0
        ? '<span class="compareprice">' + data.currencySymbol + formatPrice(data.compareAt) + "</span>"
        : "";
    var discount =
      data.discountPercent > 0
        ? '<span class="percentage-calculations">' + data.discountPercent + "% OFF</span>"
        : "";

    return (
      '<div class="swiper-slide swiper-slide-deal" data-product-id="' +
      escapeHtml(data.productId) +
      '" data-product-position="' +
      position +
      '">' +
      '<product-card class="product-card deal-product-card" data-product-id="' +
      escapeHtml(data.productId) +
      '" data-product-variants-size="' +
      data.variantsCount +
      '" data-product-handle="' +
      escapeHtml(data.handle) +
      '" data-product-variant-id="' +
      escapeHtml(data.variantId) +
      '" data-product-available="' +
      (data.isAvailable ? "true" : "false") +
      '" id="deal-product-card-' +
      escapeHtml(data.sectionId) +
      "-" +
      escapeHtml(data.slideKey) +
      '">' +
      '<a href="' +
      escapeHtml(data.url) +
      '" class="product-card__link" ref="productCardLink">' +
      '<span class="visually-hidden">' +
      escapeHtml(data.title) +
      "</span></a>" +
      '<div class="product-card__content deal-product-card__content">' +
      '<div class="deal_swiper">' +
      ageTagHtml +
      '<a href="' +
      escapeHtml(data.url) +
      '">' +
      imageHtml +
      "</a></div>" +
      '<div class="product-content-container">' +
      '<p class="product-title">' +
      escapeHtml(data.title) +
      "</p>" +
      '<div class="price-container">' +
      '<span class="mainprice">' +
      data.currencySymbol +
      formatPrice(data.price) +
      "</span>" +
      comparePrice +
      discount +
      "</div>" +
      '<div class="buy-button deal-add-to-bag">' +
      buildCtaHtml(data) +
      "</div>" +
      "</div></div></product-card></div>"
    );
  }

  /**
   * Direct add-to-cart for Fly Bundle parents. Guarded by a global flag so it never double-binds
   * with the identical handlers in quick-add.liquid / custom-product-list-cards.js.
   */
  function ensureFlyBundleHandler() {
    if (global.__flyBundleQA) return;
    global.__flyBundleQA = true;

    document.addEventListener("click", function (event) {
      var button = event.target.closest(".fly-bundle-add-btn");
      if (!button || button.classList.contains("atc-added")) return;
      event.preventDefault();
      event.stopPropagation();

      var childData;
      try {
        childData = JSON.parse(button.dataset.flyVariantIds || "");
      } catch (e) {
        return;
      }
      var offerId = button.dataset.flyOfferId;
      if (!childData || !offerId) return;

      var digitalMap = null;
      try {
        digitalMap = JSON.parse(button.dataset.flyDigitalMap || "null");
      } catch (e) {}

      var offerIdKeyed = offerId + "_" + Math.random().toString(36).substring(2, 5);
      var items = [];
      Object.keys(childData).forEach(function (key) {
        var variantId = parseInt(childData[key][0], 10);
        if (!variantId) return;
        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            "_flyBundles:OfferId": offerIdKeyed,
            "_flyBundles:prodQty": 1,
            "_flyBundles:customField": button.dataset.flyBundleName || "",
            _digital_book: digitalMap && digitalMap[String(variantId)] === true ? "true" : "false",
            _purchase_type: "null",
          },
        });
      });
      if (!items.length) return;

      button.classList.add("atc-added");
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: { data: { source: "product-form-component", itemCount: 1, isOptimistic: true } },
        })
      );
      setTimeout(function () {
        button.classList.remove("atc-added");
      }, 1500);

      fetch("/cart/add.js?app=gfbBundleApp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error(String(response.status));
        })
        .catch(function (error) {
          console.error("Deal of the Day fly bundle add error:", error);
        });
    });
  }

  function slidesSwiper(section) {
    var wrapper = section.querySelector("[data-deal-day-slides]");
    if (wrapper && wrapper.closest) {
      var fromSlides = wrapper.closest(".deal_of_the_day");
      if (fromSlides) return fromSlides;
    }
    return section.querySelector(".deal_of_the_day:not(.pre-init)") || section.querySelector(".deal_of_the_day");
  }

  function initSwiper(section, config) {
    var swiperEl = slidesSwiper(section);
    if (!swiperEl || typeof Swiper === "undefined") return null;

    var preInit = section.querySelector(".deal_of_the_day.pre-init");
    if (preInit && preInit !== swiperEl) preInit.remove();

    return new Swiper(swiperEl, {
      slidesPerView: 1.8,
      spaceBetween: 15,
      loop: config.slideCount > 1,
      effect: "coverflow",
      grabCursor: true,
      centeredSlides: true,
      pagination: {
        el: section.querySelector(".swiper-pagination"),
        clickable: true,
      },
      autoplay: config.autoplay ? { delay: 4000, pauseOnMouseEnter: true } : false,
      coverflowEffect: { rotate: 0, slideShadows: true },
      navigation: {
        nextEl: section.querySelector(".swiper-next-deal"),
        prevEl: section.querySelector(".swiper-prev-deal"),
      },
      breakpoints: {
        601: { slidesPerView: 3, spaceBetween: 10 },
      },
      on: {
        autoplayTimeLeft: function (swiper, time, progress) {
          section.querySelectorAll(".swiper-pagination-bullet").forEach(function (bullet) {
            bullet.style.setProperty("--progress", 0);
          });
          var active = section.querySelector(".swiper-pagination-bullet-active");
          if (active) active.style.setProperty("--progress", 1 - progress);
        },
      },
    });
  }

  /** Swiper is injected by theme.liquid after parse, so wait for it on a fresh load. `ready` is false on timeout. */
  function whenSwiperReady(callback) {
    var settled = false;
    function finish(ready) {
      if (settled) return;
      settled = true;
      callback(ready);
    }

    if (typeof Swiper !== "undefined") {
      finish(true);
      return;
    }

    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (typeof Swiper !== "undefined") {
        clearInterval(timer);
        finish(true);
      } else if (attempts > 150) {
        clearInterval(timer);
        finish(false);
      }
    }, 100);

    var script = document.querySelector("script[data-pw-swiper]");
    if (script) {
      script.addEventListener("load", function () {
        if (typeof Swiper === "undefined") return;
        clearInterval(timer);
        finish(true);
      });
    }

    global.addEventListener("load", function () {
      if (typeof Swiper !== "undefined") {
        clearInterval(timer);
        finish(true);
      }
    });
  }

  function DealOfTheDaySection(section) {
    this.section = section;
    this.wrapper = section.querySelector("[data-deal-day-slides]");
    this.skeleton = section.querySelector("[data-deal-day-skeleton]");
    this.handle = section.dataset.collectionHandle || "";
    this.limit = parseInt(section.dataset.productsLimit || "8", 10) || 8;
    this.autoplay = section.dataset.autoplay === "true";
    this.settings = {
      sectionId: section.dataset.sectionId || section.id,
      quickAddColorScheme: section.dataset.quickAddColorScheme || "",
      addToCartAnimation: section.dataset.addToCartAnimation || "false",
      ageTags: readAgeTags(section),
    };
  }

  DealOfTheDaySection.prototype.cacheKey = function () {
    return CACHE_KEY_PREFIX + this.handle + "_" + this.limit;
  };

  DealOfTheDaySection.prototype.readCache = function () {
    try {
      var raw = sessionStorage.getItem(this.cacheKey());
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.t == null || parsed.edges == null) return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.edges;
    } catch (e) {
      return null;
    }
  };

  DealOfTheDaySection.prototype.writeCache = function (edges) {
    try {
      sessionStorage.setItem(this.cacheKey(), JSON.stringify({ t: Date.now(), edges: edges }));
    } catch (e) {}
  };

  DealOfTheDaySection.prototype.removeSkeleton = function () {
    if (this.skeleton) {
      this.skeleton.remove();
      this.skeleton = null;
    }
  };

  DealOfTheDaySection.prototype.fail = function () {
    this.removeSkeleton();
    this.section.setAttribute("hidden", "");
    this.section.style.display = "none";
  };

  DealOfTheDaySection.prototype.render = function (edges) {
    var self = this;
    if (!edges || !edges.length) {
      this.fail();
      return;
    }

    var products = edges.map(function (edge) {
      return normalizeProduct(edge.node, self.settings);
    });

    // Coverflow needs enough slides to loop cleanly; the old Liquid duplicated the set the same way.
    var passes = products.length > 1 && products.length <= 3 ? 2 : 1;
    var html = "";
    for (var pass = 0; pass < passes; pass++) {
      for (var i = 0; i < products.length; i++) {
        var data = products[i];
        data.slideKey = pass === 0 ? String(data.productId) : data.productId + "-dup-" + pass;
        html += buildSlideHtml(data, i + 1, pass === 0 && i === 0);
      }
    }

    this.wrapper.innerHTML = html;
    ensureFlyBundleHandler();

    whenSwiperReady(function (ready) {
      if (ready) {
        initSwiper(self.section, { autoplay: self.autoplay, slideCount: products.length * passes });
      } else {
        // Without Swiper the slides stay hidden by deal-day.css, so fall back to a plain row.
        var swiperEl = slidesSwiper(self.section);
        var preInit = self.section.querySelector(".deal_of_the_day.pre-init");
        if (preInit && preInit !== swiperEl) preInit.remove();
        if (swiperEl) swiperEl.classList.add("deal_of_the_day--no-swiper");
      }
      self.removeSkeleton();
    });
  };

  DealOfTheDaySection.prototype.fetchProducts = function () {
    var self = this;
    var token = global.PW_STOREFRONT_TOKEN || "";
    if (!this.handle || !token || !this.wrapper) {
      this.fail();
      return;
    }

    var cached = this.readCache();
    if (cached) {
      this.render(cached);
      return;
    }

    fetch("/api/" + API_VERSION + "/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query: DEAL_DAY_QUERY,
        variables: { handle: this.handle, first: this.limit },
      }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Storefront API responded with " + response.status);
        return response.json();
      })
      .then(function (result) {
        var edges =
          (result.data &&
            result.data.collection &&
            result.data.collection.products &&
            result.data.collection.products.edges) ||
          [];
        // Prefer rendering products even if Shopify returns non-fatal GraphQL errors.
        if (edges.length) {
          if (result.errors && result.errors.length) {
            console.warn("Deal of the Day GraphQL warnings:", result.errors);
          }
          self.writeCache(edges);
          self.render(edges);
          return;
        }
        if (result.errors && result.errors.length) {
          throw new Error(result.errors[0] && result.errors[0].message);
        }
        self.render(edges);
      })
      .catch(function (error) {
        console.error("Deal of the Day load failed:", error);
        self.fail();
      });
  };

  DealOfTheDaySection.prototype.start = function () {
    var self = this;
    if (typeof IntersectionObserver === "undefined") {
      this.fetchProducts();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries[0] || !entries[0].isIntersecting) return;
        observer.disconnect();
        self.fetchProducts();
      },
      { rootMargin: PREFETCH_ROOT_MARGIN }
    );
    observer.observe(this.section);
  };

  function boot() {
    document.querySelectorAll("[data-section-type='deal_of_the_day']").forEach(function (section) {
      if (section.dataset.dealDayInitialized === "true") return;
      section.dataset.dealDayInitialized = "true";
      new DealOfTheDaySection(section).start();
    });
  }

  global.__dealOfTheDayBoot = boot;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
  global.addEventListener("pageshow", boot);
})(typeof window !== "undefined" ? window : this);
